import { changedControlSchema, controlListItemSchema } from "@/generated/compliance-control-contract";
/**
 * Compliance reports — Zod schemas.
 *
 * Reports are server-generated exports: the operator asks for a
 * report (type + date range), the backend queues a job, and the
 * UI polls for completion and surfaces a download link.
 *
 * Everything here is read-only from the UI's perspective except
 * the "request a new export" mutation.
 */

import { z } from "zod";
import { REPORT_KINDS } from "@pureid/ui";
import { availableFrameworkSchema, frameworkSelectionSchema } from "@/generated/compliance-selection-contract";
export { availableFrameworkSchema, frameworkSelectionSchema };

import { controlDetailSchema, crosswalkEntrySchema, controlOverrideDetailSchema } from "@/generated/compliance-control-contract";
export { controlDetailSchema, crosswalkEntrySchema, controlOverrideDetailSchema };

import { scopeRequestSchema, scopeRequestListSchema } from "@/generated/compliance-scope-request-contract";
export { scopeRequestSchema, scopeRequestListSchema };

// The posture, register, review and seat responses are the contract's own
// schemas, generated into compliance-posture-contract.ts by make docs. This
// module re-exports them and derives UI vocabularies from them; it never
// restates a documented response.
import {
  actorScopeSchema,
  attestationRequestListSchema,
  attestationRequestSchema,
  auditorAccessLogEntrySchema,
  auditorAccessLogListSchema,
  auditorTokenListSchema,
  auditorTokenSchema,
  checkStatusSchema,
  confidenceCountsSchema,
  controlHistoryEntrySchema,
  controlHistoryResponseSchema,
  counterSignatureSchema,
  coverageIndexSchema,
  dashboardActiveExceptionSchema,
  dashboardCountsSchema,
  dashboardFrameworkSchema,
  dashboardHeatmapCellSchema,
  dashboardStatusSchema,
  dashboardSummarySchema,
  dashboardTopIssueSchema,
  findingsPageSchema,
  mandateClauseSchema,
  mandateMeasureSchema,
  mandateRuleSetSchema,
  mandateRuleSetsResponseSchema,
  pendingCloseListSchema,
  pendingCloseSchema,
  periodCloseListSchema,
  periodCloseSchema,
  questionnaireResponseSchema,
  questionnaireRowSchema,
  questionnaireSectionSchema,
  readinessCheckSchema,
  readinessReportSchema,
  refreshStatusSchema,
  reportEvidenceRefSchema,
  requestStatusSchema,
  transparencyWitnessListSchema,
  transparencyWitnessSchema,
  treatmentCountsSchema,
  trendPointSchema,
  trendSeriesSchema,
} from "@/generated/compliance-posture-contract";
export {
  actorScopeSchema,
  attestationRequestListSchema,
  attestationRequestSchema,
  auditorAccessLogEntrySchema,
  auditorAccessLogListSchema,
  auditorTokenListSchema,
  auditorTokenSchema,
  checkStatusSchema,
  confidenceCountsSchema,
  controlHistoryEntrySchema,
  controlHistoryResponseSchema,
  counterSignatureSchema,
  coverageIndexSchema,
  dashboardActiveExceptionSchema,
  dashboardCountsSchema,
  dashboardFrameworkSchema,
  dashboardHeatmapCellSchema,
  dashboardStatusSchema,
  dashboardSummarySchema,
  dashboardTopIssueSchema,
  findingsPageSchema,
  mandateClauseSchema,
  mandateMeasureSchema,
  mandateRuleSetSchema,
  mandateRuleSetsResponseSchema,
  pendingCloseListSchema,
  pendingCloseSchema,
  periodCloseListSchema,
  periodCloseSchema,
  questionnaireResponseSchema,
  questionnaireRowSchema,
  questionnaireSectionSchema,
  readinessCheckSchema,
  readinessReportSchema,
  refreshStatusSchema,
  reportEvidenceRefSchema,
  requestStatusSchema,
  transparencyWitnessListSchema,
  transparencyWitnessSchema,
  treatmentCountsSchema,
  trendPointSchema,
  trendSeriesSchema,
};

// THE CLOSED SET COMES FROM ONE PLACE IN THE FRONTEND.
//
// It used to be written out here and again, partially, in the portal's
// auditor seat. Both apps now read `REPORT_KINDS` from the shared
// package, so a kind that is added and not named fails the build rather
// than reaching an auditor as a raw identifier.
export const reportKindSchema = z.enum(REPORT_KINDS);
export type ReportKind = z.infer<typeof reportKindSchema>;

/** Kinds whose run is a page to read, not only a file to download. */
export const DOCUMENT_REPORT_KINDS: ReadonlySet<ReportKind> = new Set<ReportKind>([
  "auth_mandate_readiness",
]);

// xlsx is a single-sheet workbook whose cells are all inline strings, so
// Excel cannot reinterpret a control id as a date or a digit-leading hash
// as scientific notation. CSV stays the default: it streams, and it is the
// format that always works.
// A counter-signature names the exported bytes, not the report row: a
// signature over a row means "I reviewed whatever that row points at now",
// while a signature over a hash is something anyone holding the file can
// check with sha256sum.
export const reportCounterSignatureFields = {
  content_sha256: z.string().optional().default(""),
  counter_signed_by_email: z.string().nullable().optional(),
  counter_signed_at: z.string().nullable().optional(),
};

export const reportFormatSchema = z.enum(["csv", "xlsx", "jsonl", "pdf"]);
export type ReportFormat = z.infer<typeof reportFormatSchema>;

export const reportStatusSchema = z.enum([
  "queued",
  "running",
  "ready",
  "failed",
  "expired",
]);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

export const reportSchema = z.object({
  id: z.string(),
  kind: reportKindSchema,
  format: reportFormatSchema,
  status: reportStatusSchema,
  requested_at: z.string(),
  completed_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  row_count: z.number().int().nonnegative().nullable(),
  size_bytes: z.number().int().nonnegative().nullable(),
  error: z.string().nullable(),
  from_date: z.string(),
  to_date: z.string(),
  download_url: z.string().nullable(),
  requested_by_email: z.string().nullable(),
  // The run's kind-specific parameters, printed on the run record beside
  // the period so a reader can reproduce it. Empty for a listing kind.
  params: z.record(z.string(), z.unknown()).default({}),
  ...reportCounterSignatureFields,
});
export type Report = z.infer<typeof reportSchema>;

export const reportsListResponseSchema = z.object({
  data: z.array(reportSchema),
});
export type ReportsListResponse = z.infer<typeof reportsListResponseSchema>;

export const requestReportSchema = z.object({
  kind: reportKindSchema,
  format: reportFormatSchema,
  from_date: z.string().min(1),
  to_date: z.string().min(1),
  params: z.record(z.string(), z.unknown()).optional(),
});
export type RequestReportInput = z.infer<typeof requestReportSchema>;

// ── The three axes ───────────────────────────────────────────────────────
//
// A control carries three independent facts, and this file keeps them in
// three fields because collapsing them into one word is what made the old
// surface unreadable:
//
//   how we know — Measured · By design · Attested · Not checked yet
//   result      — Met · Partly · Not met
//   treatment   — none · Exception · Not applicable
//
// A treatment is not a result. An exception does not make a failing
// control pass; it records that someone accepted the failure for a stated
// period with a stated compensating measure. Render them separately, and
// never derive one from another.
//
// `complianceStatusSchema` used to sit here — a second, incompatible
// verdict enum ("partially_compliant" rather than "partial") with no
// remaining reader. Two enums for one domain concept is how a surface ends
// up rendering the same control two ways; it is gone.

export type DashboardStatus = z.infer<typeof dashboardStatusSchema>;

// How we know. "structural" renders as "By design" — a property inherent
// in how the platform is built, read from its configuration rather than
// from tenant state. The empty string is a control nobody has evaluated:
// the server used to send "auto" for those, so an unchecked control
// claimed it had been measured.
export const dashboardConfidenceSchema = z.enum([
  "auto",
  "structural",
  "manual",
  "override",
  "",
]);
export type DashboardConfidence = z.infer<typeof dashboardConfidenceSchema>;

// What the operator decided about a control, independent of the verdict.
// Absent means nothing has been decided.
export const dashboardTreatmentSchema = z.enum([
  "none",
  "exception",
  "not_applicable",
]);
export type DashboardTreatment = z.infer<typeof dashboardTreatmentSchema>;

// The one number, and the counts that make it readable. A bare index is
// never rendered on its own: "87" says nothing without "of what, checked
// how, out of how many".
// The other two axes in aggregate. A reader weighing the headline needs
// these: "87, and 40 of those are design assertions nobody has exercised"
// is a different picture from "87, all measured this morning", and the
// index alone cannot tell them apart.
export type ConfidenceCounts = z.infer<typeof confidenceCountsSchema>;
export type TreatmentCounts = z.infer<typeof treatmentCountsSchema>;
export type CoverageIndex = z.infer<typeof coverageIndexSchema>;
export type DashboardCounts = z.infer<typeof dashboardCountsSchema>;

// Health is bucketed by the server (good/warning/critical) so the
// frontend doesn't re-derive it per render.
export const dashboardHealthSchema = dashboardFrameworkSchema.shape.health;
export type DashboardHealth = z.infer<typeof dashboardHealthSchema>;

export type DashboardFramework = z.infer<typeof dashboardFrameworkSchema>;
export type DashboardTopIssue = z.infer<typeof dashboardTopIssueSchema>;

// ── Findings datatable (paginated) ────────────────────────────
//
// Backend: GET /compliance/findings (see posture/admin_handler.go).
// Powers the findings table on the compliance dashboard with
// search, sortable column headers, and infinite scroll.

export const findingsSortSchema = z.enum([
  "severity_desc",
  "severity_asc",
  "control_asc",
  "control_desc",
  "framework_asc",
  "framework_desc",
  "status_desc",
  "status_asc",
  "title_asc",
  "title_desc",
]);
export type FindingsSort = z.infer<typeof findingsSortSchema>;

export type FindingsPage = z.infer<typeof findingsPageSchema>;

export type DashboardHeatmapCell = z.infer<typeof dashboardHeatmapCellSchema>;

// ActiveException is one entry in the dashboard's "Exceptions"
// tab — a control whose verdict is held open by a time-boxed
// override. Used for countdown rendering + drill-through.
export type DashboardActiveException = z.infer<typeof dashboardActiveExceptionSchema>;

// The dashboard response's crosswalk matrix is a property of the catalog's
// mappings, identical for every tenant. The console does not render it: on a
// per-tenant page it would read as a statement about that tenant's evidence
// reuse, which it is not.

// ── Evidence locker ───────────────────────────────────────────────────────
//
// Operator-uploaded artefacts (runbooks, vendor reports, screenshots)
// linked to compliance controls. Many-to-many: one artefact can support
// many controls; one control can have many artefacts.

import { evidenceArtefactSchema, evidenceLinkCreateSchema, evidenceListResponseSchema, evidenceMetadataUpdateSchema, evidenceSensitivitySchema } from "@/generated/compliance-evidence-contract";
export { evidenceArtefactSchema, evidenceLinkCreateSchema, evidenceListResponseSchema, evidenceMetadataUpdateSchema, evidenceSensitivitySchema };
export type EvidenceArtefact = z.infer<typeof evidenceArtefactSchema>;
export type EvidenceListResponse = z.infer<typeof evidenceListResponseSchema>;
export type EvidenceMetadataUpdate = z.infer<typeof evidenceMetadataUpdateSchema>;
export type EvidenceLinkCreate = z.infer<typeof evidenceLinkCreateSchema>;
export type EvidenceSensitivity = EvidenceArtefact["sensitivity"];

/** Who may see a document, in the words a person reads; the order is the picker's. */
export const evidenceSensitivityOptions: ReadonlyArray<{ value: EvidenceSensitivity; label: string; hint: string }> = [
  { value: "auditor_only", label: "Auditor only", hint: "Goes into an external auditor's packet." },
  { value: "public_safe", label: "Public safe", hint: "Goes into an auditor's packet and may be shared outside it." },
  { value: "internal_only", label: "Internal only", hint: "Stays with your administrators and never goes into an auditor's packet." },
];

// Refresh-queue status surfaced alongside the dashboard payload and
// also as a standalone /compliance/refresh-status endpoint. The UI uses
// it to render "Last assessed N ago / Refresh now / Refreshing…" without
// round-trip storms. tenant_id is NOT on the wire — it's redundant with
// DashboardSummary.tenant_id and the URL the request was made against.
export type RefreshStatus = z.infer<typeof refreshStatusSchema>;

// One in-scope control a report kind stands behind — the report↔register
// bridge, derived server-side from the evaluator map. See the report
// catalogue: a card that cannot say which obligations its rows evidence
// sits beside the register unmapped, in both directions.
export type ReportEvidenceRef = z.infer<typeof reportEvidenceRefSchema>;

export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;

// Who acted, at which level. "tenant" is a home admin and prints nothing
// extra; the other two are printed beside the email so a reader of the
// record can tell a delegated administrator or PureID itself from the
// tenant's own people. See actorLabel in labels.ts.
export type ActorScope = z.infer<typeof actorScopeSchema>;

// Why a pack is not offered. Present only when offered is false.
export const notOfferedReasonSchema = z.enum([
  "internal",
  "licence_unreviewed",
  "licence_blocked",
]);
export type NotOfferedReason = z.infer<typeof notOfferedReasonSchema>;

export const licenceStatusSchema = z.enum(["reviewed", "unreviewed", "blocked"]);
export type LicenceStatus = z.infer<typeof licenceStatusSchema>;

// Whether the pack's statutory citations have been checked, and by whom.
// Printed on every card and the framework page that is not counsel-verified.
export const verificationSchema = z.enum([
  "not_applicable",
  "unverified",
  "engineering_verified",
  "counsel_verified",
]);
export type Verification = z.infer<typeof verificationSchema>;

// Per-framework drill-in
export { controlListItemSchema };
export type ControlListItem = z.infer<typeof controlListItemSchema>;

// One line of the shared-responsibility split. `ours` is resolved on the
// wire, so the surface renders a fact rather than interpreting an absence.
export const frameworkObligationSchema = z.object({
  title: z.string(),
  detail: z.string(),
  ours: z.boolean(),
});
export type FrameworkObligation = z.infer<typeof frameworkObligationSchema>;

export const frameworkDetailSchema = z.object({
  framework_id: z.string(),
  framework_version: z.string(),
  name: z.string(),
  description: z.string(),
  // Whether this framework is part of what the tenant answers to. The
  // register is readable before selection — the chooser links here so
  // an operator can read the obligations before taking them on — and
  // the page says which kind of register it is showing. Defaulted true
  // for deploy skew: an older backend only ever served selected
  // frameworks, so absence means in scope.
  selected: z.boolean().optional().default(true),
  // Whether the pack can be added to scope today, and the state of its
  // licence and citation review. Defaulted for the same deploy skew as
  // `selected`: an older backend served only packs it offered.
  offered: z.boolean().optional().default(true),
  licence_status: licenceStatusSchema.optional().default("reviewed"),
  verification: verificationSchema.optional().default("not_applicable"),
  counts: dashboardCountsSchema,
  coverage_index: coverageIndexSchema,
  // The other two axes, scoped to this framework. A coverage number
  // without them asks the reader to assume how much of it is measured.
  by_confidence: confidenceCountsSchema,
  treatments: treatmentCountsSchema,
  // The denominator, and why we ship this numerator. Zero when the pack
  // carries the whole instrument — the surface then says nothing rather
  // than inventing one.
  instrument_total: z.number().int().nonnegative().optional().default(0),
  subset_rationale: z.string().optional().default(""),
  responsibilities_lede: z.string().optional().default(""),
  // .nullable() is load-bearing: Zod applies a default to `undefined`
  // only, so a `null` from a pack with no responsibilities block threw in
  // the route loader and took the whole framework page down. The backend
  // now sends `[]`; this tolerates both, because a client that trusts one
  // server shape is one deploy-skew away from the same crash.
  responsibilities: z
    .array(frameworkObligationSchema)
    .nullable()
    .optional()
    .default([])
    .transform((v) => v ?? []),
  controls: z.array(controlListItemSchema),
});
export type FrameworkDetail = z.infer<typeof frameworkDetailSchema>;

// Per-control detail
export type CrosswalkEntry = z.infer<typeof crosswalkEntrySchema>;
export type ControlOverrideDetail = z.infer<typeof controlOverrideDetailSchema>;
export type ControlDetail = z.infer<typeof controlDetailSchema>;

// setOverrideInputSchema is GONE, with the endpoint and the mutation.
//
// It was one union covering two acts that turned out to be different in
// kind: marking a control not applicable, which removes it from the
// denominator and now takes two people through a request, and granting a
// time-boxed exception, which leaves the gap counted and has always had a
// named approver. Modelling them as one shape is part of how the
// exception branch came to post to an endpoint that refuses it.

// ── Phase E: auditor portal tokens ─────────────────────────────────────

export const auditorTokenStatusSchema = auditorTokenSchema.shape.status;
export type AuditorTokenStatus = z.infer<typeof auditorTokenStatusSchema>;

// A party outside this deployment that holds copies of the transparency
// log's signed tree heads.
//
// The register answers two questions and the second is the one that
// matters: who did we ask, and have they actually been watching. A key on
// file with no recent signature is a party that was asked and has not
// answered, which is a finding rather than a blank.
export type TransparencyWitness = z.infer<typeof transparencyWitnessSchema>;

export type TransparencyWitnessList = z.infer<typeof transparencyWitnessListSchema>;

// A 32-byte Ed25519 public key, as the witness printed it: 64 hex
// characters or base64. Anything else is refused rather than coerced.
export const registerWitnessSchema = z.object({
  name: z.string().trim().min(1, "Name the party that will hold the copies").max(200),
  public_key: z
    .string()
    .trim()
    .min(1, "Paste the public key the witness printed when it started")
    .refine(
      (v) => /^[0-9a-fA-F]{64}$/.test(v) || /^[A-Za-z0-9+/]{42,44}={0,2}$/.test(v),
      "That is not a 32-byte Ed25519 public key",
    ),
});
export type RegisterWitnessInput = z.infer<typeof registerWitnessSchema>;

export type AuditorToken = z.infer<typeof auditorTokenSchema>;

export type AuditorTokenList = z.infer<typeof auditorTokenListSchema>;

export const issueAuditorTokenInputSchema = z.object({
  description: z
    .string()
    .min(1, "Description is required")
    .max(256, "Description must be 256 characters or fewer"),
  // Server-side: must be RFC3339 in [now+1min, now+90d].
  expires_at: z.string(),
});
export type IssueAuditorTokenInput = z.infer<typeof issueAuditorTokenInputSchema>;

export type AuditorAccessLogEntry = z.infer<typeof auditorAccessLogEntrySchema>;

export type AuditorAccessLogList = z.infer<typeof auditorAccessLogListSchema>;

// ── Phase G: trend + what-changed feed ─────────────────────────────────

export type TrendPoint = z.infer<typeof trendPointSchema>;

export type TrendSeries = z.infer<typeof trendSeriesSchema>;

export { changedControlSchema };
export type ChangedControl = z.infer<typeof changedControlSchema>;

export const changedControlsListSchema = z.object({
  data: z.array(changedControlSchema),
  since: z.string(),
});
export type ChangedControlsList = z.infer<typeof changedControlsListSchema>;

// ── Phase H: audit-readiness ───────────────────────────────────────────

export type CheckStatus = z.infer<typeof checkStatusSchema>;

export type ReadinessCheck = z.infer<typeof readinessCheckSchema>;

export type ReadinessReport = z.infer<typeof readinessReportSchema>;

// ── Phase I: questionnaire auto-fill ───────────────────────────────────

export const questionnaireFormatSchema = questionnaireResponseSchema.shape.format;
export type QuestionnaireFormat = z.infer<typeof questionnaireFormatSchema>;

export type QuestionnaireRow = z.infer<typeof questionnaireRowSchema>;

export type QuestionnaireSection = z.infer<typeof questionnaireSectionSchema>;

export type QuestionnaireResponse = z.infer<typeof questionnaireResponseSchema>;


// ── Two-person scope exclusion ───────────────────────────────────────────
//
// Marking a control not applicable removes it from the denominator
// entirely. It used to be one person's click; it is now a request a
// colleague decides, and the single-person endpoint is gone rather than
// deprecated — leaving it would have made the two-person path optional.
//
// A rejected request is kept, with its reason: "we considered scoping this
// out and decided not to" is what an examination asks about.

export const requestKindSchema = z.enum(["scope_exclusion", "exception"]);
export type RequestKind = z.infer<typeof requestKindSchema>;
export type RequestStatus = z.infer<typeof requestStatusSchema>;

export type ScopeRequest = z.infer<typeof scopeRequestSchema>;

// ── The framework chooser ────────────────────────────────────────────────
//
// Name, blurb and region come from the pack, beside the controls they
// describe — a chooser blurb that promises something the controls do not
// check is a promise made at the very moment of selection.

export type AvailableFramework = z.infer<typeof availableFrameworkSchema>;
export type FrameworkSelection = z.infer<typeof frameworkSelectionSchema>;

// ── One control's own record ──────────────────────────────────────────
//
// Every snapshot of this control, newest first, each with the chain hash
// it was sealed with. The hash is what makes this a record rather than a
// log: it is the same hash the signed packet carries, and anyone holding
// the packet can check it.
export type ControlHistoryEntry = z.infer<typeof controlHistoryEntrySchema>;

// Naming who answers for a control. Both people are optional and both
// roles are required alongside their person; an explicit null clears.
export const setOwnershipSchema = z.object({
  owner_admin_id: z.string().uuid().nullable(),
  owner_role: z.string(),
  reviewer_admin_id: z.string().uuid().nullable(),
  reviewer_role: z.string(),
});
export type SetOwnershipInput = z.infer<typeof setOwnershipSchema>;

// ── Attestation ───────────────────────────────────────────────────────
//
// A person's statement that a control holds, standing until an expiry,
// reviewed by a second person, and resting on at least one document. All
// four are required by the server; the form enforces the same four so the
// reason is visible before the request rather than in a toast after it.
export const MIN_ATTESTATION_STATEMENT = 20;
export const MAX_ATTESTATION_MONTHS = 12;

export const attestControlSchema = z.object({
  statement: z.string().min(MIN_ATTESTATION_STATEMENT),
  expires_at: z.string().min(1),
  reviewer_admin_id: z.string().uuid(),
  artefact_ids: z.array(z.string().uuid()).min(1),
});
export type AttestControlInput = z.infer<typeof attestControlSchema>;

// What the counter-signature endpoint returns: the reviewer, the instant,
// and the bytes they signed over. The hash is echoed back because a
// signature over a row would mean "I reviewed whatever that row points at
// now" — over a hash it is something anyone holding the file can check.
export type CounterSignature = z.infer<typeof counterSignatureSchema>;

// ── Attestations waiting on a second person ──────────────────────────
//
// An attestation is offered and only becomes evidence when a colleague
// accepts it from their own session. Until then the control reads as it
// did — so this list is the only place the pending state is visible, and
// without it an offered attestation would vanish until somebody guessed
// to look.
export type AttestationRequest = z.infer<typeof attestationRequestSchema>;

// ── Period closes waiting on a second person ─────────────────────────
//
// The packet is frozen and hashed when the close is offered; the
// signature is applied at the decision, over that same root. The hash is
// on this row because the reviewer is approving specific bytes, and
// showing it is what makes "I reviewed this packet" checkable.
export type PendingClose = z.infer<typeof pendingCloseSchema>;

// ── The closes that were signed ──────────────────────────────────────
//
// Append-only by trigger: a close is a statement two people put their
// names to, and re-closing writes a new row rather than editing one. The
// signature is over the manifest root, which anyone holding the packet
// re-derives without asking us — which is why both are on the row.
export type PeriodClose = z.infer<typeof periodCloseSchema>;

// ── Authentication-mandate readiness ─────────────────────────────────────
//
// The document behind docs/product-areas.md. The wire
// shape is the worker's own: every count carries its denominator, and a
// clause with a zero denominator carries a sentence (has_fraction false).

export type MandateRuleSet = z.infer<typeof mandateRuleSetSchema>;

export const mandateHowKnownSchema = z.enum(["measured", "attested", "by_design", "not_checked"]);
export type MandateHowKnown = z.infer<typeof mandateHowKnownSchema>;
export const mandateResultSchema = z.enum(["met", "partly", "not_met", ""]);
export type MandateResult = z.infer<typeof mandateResultSchema>;

const mandateGroupSchema = z.object({ key: z.string(), label: z.string(), count: z.number().int() });

export const mandateClauseCardSchema = z.object({
  clause: mandateClauseSchema,
  how_known: mandateHowKnownSchema,
  result: mandateResultSchema.optional().default(""),
  has_fraction: z.boolean(),
  numerator: z.number().int(),
  denominator: z.number().int(),
  unit: z.string(),
  statement: z.string(),
  detail: z.string(),
  control: z
    .object({
      catalog_id: z.string(),
      framework_id: z.string(),
      framework_version: z.string(),
      control_id: z.string(),
      title: z.string(),
    })
    .nullable()
    .optional(),
  attestation: z
    .object({
      reviewer_email: z.string(),
      statement: z.string(),
      expires_at: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type MandateClauseCard = z.infer<typeof mandateClauseCardSchema>;

export const mandateDocumentSchema = z.object({
  kind: z.literal("auth_mandate_readiness"),
  rule_set: mandateRuleSetSchema,
  period_from: z.string(),
  period_to: z.string(),
  generated_at: z.string(),
  population: z.object({
    as_of: z.string(),
    total: z.number().int(),
    groups: z.array(mandateGroupSchema),
    excluded: z.array(mandateGroupSchema),
    note: z.string(),
  }),
  policy: z.object({
    tenant_methods: z.array(z.string()),
    platform_methods: z.array(z.string()),
    passkey_required: z.boolean(),
  }),
  clauses: z.array(mandateClauseCardSchema),
  outside_rule: z.array(
    z.object({
      severity: z.enum(["warn", "bad"]),
      count: z.number().int(),
      sentence: z.string(),
      why: z.string(),
      list_key: z.string(),
    }),
  ),
  methods: z.array(
    z.object({
      method: z.string(),
      label: z.string(),
      phishing_resistant: z.boolean(),
      otp_class: z.boolean(),
      sign_ins: z.number().int(),
      people: z.number().int(),
    }),
  ),
});
export type MandateDocument = z.infer<typeof mandateDocumentSchema>;
